import React, { useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FileEarmarkPdfFill } from 'react-bootstrap-icons';
import { Paperclip, Eye, Trash2 } from 'lucide-react';
import { readFile } from '../../helpers/api';
import { toast } from 'sonner';
import AlertModal from '../AlertModal';

const attachBtnStyle = {
  gap: 6,
  padding: '6px 12px',
  border: '1px dashed #ced4da',
  borderRadius: 8,
  background: '#f9f9f8',
  fontSize: '12.5px',
  color: '#5f5e5a',
  width: 'fit-content',
  userSelect: 'none',
};

const chipStyle = {
  gap: 6,
  padding: '4px 10px',
  border: '0.5px solid rgba(0,0,0,0.12)',
  borderRadius: 8,
  background: '#f9f9f8',
  fontSize: 12,
  color: '#5f5e5a',
  maxWidth: 180,
};

const FileRow = ({ label, onPreview, onRemove }) => (
  <div className="d-flex align-items-center justify-content-between border rounded bg-white text-dark p-1 mb-1">
    <div className="d-flex align-items-center flex-grow-1 overflow-hidden pl-1" style={{ minWidth: 0 }}>
      <FileEarmarkPdfFill size={14} className="text-danger flex-shrink-0" />
      <div className="xsmallFont text-truncate ml-2">{label}</div>
    </div>
    <div className="d-flex align-items-center flex-shrink-0">
      {onPreview && (
        <Button
          variant="light"
          size="sm"
          onClick={e => {
            e.stopPropagation();
            onPreview();
          }}
          className="p-1 border-0 d-flex align-items-center justify-content-center"
          title="Preview file"
        >
          <Eye size={12} />
        </Button>
      )}
      <Button
        variant="light"
        size="sm"
        onClick={e => {
          e.stopPropagation();
          onRemove();
        }}
        className="p-1 text-danger border-0 d-flex align-items-center justify-content-center"
        title="Remove file"
      >
        <Trash2 size={12} />
      </Button>
    </div>
  </div>
);

const FileChip = ({ label, onPreview, onRemove }) => (
  <span className="d-inline-flex align-items-center" style={chipStyle} title={label}>
    <FileEarmarkPdfFill size={11} className="text-danger flex-shrink-0" />
    <span className="text-truncate" style={{ fontSize: 11 }}>
      {label}
    </span>
    {onPreview && (
      <button
        type="button"
        className="border-0 bg-transparent p-0 d-inline-flex align-items-center"
        style={{ color: '#888780', lineHeight: 1 }}
        title="Preview file"
        onClick={e => {
          e.stopPropagation();
          onPreview();
        }}
      >
        <Eye size={11} />
      </button>
    )}
    <button
      type="button"
      className="border-0 bg-transparent p-0 d-inline-flex align-items-center"
      style={{ color: '#888780', lineHeight: 1, fontSize: 12 }}
      title="Remove file"
      onClick={e => {
        e.stopPropagation();
        onRemove();
      }}
    >
      ✕
    </button>
  </span>
);

const InstructionFilesUpload = ({
  files = [],
  pendingFiles = [],
  onFilesChange,
  onPendingFilesChange,
  onRemovedFileIdsChange,
  disabled = false,
  compact = false,
}) => {
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [deleteCandidateFileId, setDeleteCandidateFileId] = useState(null);

  const handleFiles = fileList => {
    const file = fileList?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are supported');
      return;
    }

    onPendingFilesChange?.([...(pendingFiles || []), file]);
  };

  const handleFileInputChange = e => {
    handleFiles(e.target.files);
    e.target.value = '';
  };

  const handlePreviewFile = async file => {
    const fileName = file?.filename;
    if (!fileName) {
      toast.error('Failed to preview file');
      return;
    }

    const { response, error } = await readFile({
      fileName,
      mimeType: file?.contentType || 'application/pdf',
    });

    if (error) {
      toast.error(error);
      return;
    }

    window.open(response, '_blank', 'noopener,noreferrer');
  };

  const handleConfirmDelete = () => {
    if (!deleteCandidateFileId) return;
    const fileId = deleteCandidateFileId;
    setDeleteCandidateFileId(null);
    onFilesChange?.((files || []).filter(file => String(file?._id || file) !== String(fileId)));
    onRemovedFileIdsChange?.(fileId);
  };

  const handleRemovePending = index => {
    onPendingFilesChange?.((pendingFiles || []).filter((_, i) => i !== index));
  };

  const fileItems = [
    ...files.map(file => {
      const fileId = String(file?._id || file);
      return {
        key: fileId,
        label: file?.metadata?.originalName || file?.filename || `File ${fileId.slice(-6)}`,
        onPreview: () => handlePreviewFile(file),
        onRemove: () => setDeleteCandidateFileId(fileId),
      };
    }),
    ...pendingFiles.map((file, index) => ({
      key: `pending-${index}`,
      label: file.name,
      onPreview: null,
      onRemove: () => handleRemovePending(index),
    })),
  ];

  if (compact) {
    return (
      <>
        <div className="d-flex flex-column align-items-start" style={{ gap: 4 }}>
          <label
            className="d-inline-flex align-items-center mb-0"
            style={{
              ...attachBtnStyle,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
              borderColor: isDragOver ? '#185fa5' : attachBtnStyle.border,
              background: isDragOver ? '#e6f1fb' : attachBtnStyle.background,
              color: isDragOver ? '#185fa5' : attachBtnStyle.color,
            }}
            onClick={() => !disabled && fileInputRef.current?.click()}
            onDragOver={e => {
              e.preventDefault();
              if (!disabled) setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={e => {
              e.preventDefault();
              setIsDragOver(false);
              if (!disabled) handleFiles(e.dataTransfer?.files);
            }}
          >
            <Paperclip size={14} />
            Attach PDF
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileInputChange}
              className="d-none"
              disabled={disabled}
              onClick={e => e.stopPropagation()}
            />
          </label>
          {fileItems.length > 0 && (
            <div className="d-flex flex-wrap" style={{ gap: 4 }}>
              {fileItems.map(item => (
                <FileChip
                  key={item.key}
                  label={item.label}
                  onPreview={item.onPreview}
                  onRemove={item.onRemove}
                />
              ))}
            </div>
          )}
        </div>

        <AlertModal
          show={deleteCandidateFileId !== null}
          onHide={() => setDeleteCandidateFileId(null)}
          onDismissClick={() => setDeleteCandidateFileId(null)}
          onContinueClick={handleConfirmDelete}
          alertText="This PDF will be removed from the directory when you save the order. Canceling the form keeps the file."
          dismissButtonText="Cancel"
          continueButtonText="Remove"
          dismissButtonVariant="secondary"
          continueButtonVariant="danger"
        />
      </>
    );
  }

  return (
    <>
      <div
        onDragOver={e => {
          e.preventDefault();
          if (!disabled) setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={e => {
          e.preventDefault();
          setIsDragOver(false);
          if (!disabled) handleFiles(e.dataTransfer?.files);
        }}
        onClick={() => !disabled && fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${isDragOver ? '#16a34a' : '#d1d5db'}`,
          borderRadius: '8px',
          padding: '16px',
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: isDragOver ? '#f0fdf4' : '#fafafa',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <Paperclip size={18} className="mb-1" />
        <div style={{ fontSize: '13px', fontWeight: 500 }}>Drag and drop PDF here</div>
        <div style={{ fontSize: '11px', color: '#64748b' }}>Saved when you save the order</div>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          onChange={handleFileInputChange}
          className="d-none"
          disabled={disabled}
        />
      </div>

      <small className="text-muted mt-1 d-block">Only PDF files are supported.</small>

      {(files.length > 0 || pendingFiles.length > 0) && (
        <div className="mt-2">
          <small className="text-muted d-block mb-1">Uploaded PDFs</small>
          <div className="d-flex flex-column">
            {files.map(file => {
              const fileId = String(file?._id || file);
              const fileLabel = file?.metadata?.originalName || file?.filename || `File ${fileId.slice(-6)}`;

              return (
                <FileRow
                  key={fileId}
                  label={fileLabel}
                  onPreview={() => handlePreviewFile(file)}
                  onRemove={() => setDeleteCandidateFileId(fileId)}
                />
              );
            })}
            {pendingFiles.map((file, index) => (
              <FileRow
                key={`pending-${index}`}
                label={file.name}
                onRemove={() => handleRemovePending(index)}
              />
            ))}
          </div>
        </div>
      )}

      <AlertModal
        show={deleteCandidateFileId !== null}
        onHide={() => setDeleteCandidateFileId(null)}
        onDismissClick={() => setDeleteCandidateFileId(null)}
        onContinueClick={handleConfirmDelete}
        alertText="This PDF will be removed from the directory when you save the order. Canceling the form keeps the file."
        dismissButtonText="Cancel"
        continueButtonText="Remove"
        dismissButtonVariant="secondary"
        continueButtonVariant="danger"
      />
    </>
  );
};

export default InstructionFilesUpload;
